import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { ThemeToggle } from "@/components/ThemeToggle";
import { EngineBadge, LoginBanner } from "@/components/DayBits";
import { Activity, FlaskConical, History, Settings, Lock, Plug, PlugZap, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";

const NAV = [
  { to: "/", label: "LVRC", icon: Activity, id: "nav-live" },
  { to: "/backtest", label: "Backtest", icon: FlaskConical, id: "nav-backtest" },
  { to: "/calendar", label: "P&L", icon: CalendarDays, id: "nav-calendar" },
  { to: "/history", label: "History", icon: History, id: "nav-history" },
  { to: "/settings", label: "Settings", icon: Settings, id: "nav-settings" },
];

function useIstClock() {
  const [t, setT] = useState("");
  useEffect(() => {
    const f = () => setT(new Date().toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour12: false }));
    f();
    const i = setInterval(f, 1000);
    return () => clearInterval(i);
  }, []);
  return t;
}

export function useKiteStatus() {
  const [s, setS] = useState(null);
  const load = () => api.get("/kite/status").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { load(); const i = setInterval(load, 60000); return () => clearInterval(i); }, []);
  return [s, load];
}

export default function Shell({ children, onLock }) {
  const clock = useIstClock();
  const [kite] = useKiteStatus();
  const on = kite?.logged_in;
  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <header className="sticky top-0 z-40 border-b border-[#1E293B] bg-[#090D16]/85 backdrop-blur-xl">
        <div className="max-w-[1500px] mx-auto px-4 h-14 flex items-center gap-4">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-lg font-bold tracking-tight text-slate-100" data-testid="brand">LVRC</span>
            <span className="hidden sm:inline label-xs">Lowest-Volume Reversal</span>
          </div>
          <nav className="hidden md:flex items-center gap-1 ml-4">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end data-testid={n.id}
                className={({ isActive }) => `px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${isActive ? "bg-[#1A2235] text-cyan-300" : "text-slate-400 hover:text-slate-100"}`}>
                <n.icon size={14} />{n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <EngineBadge />
            <span data-testid="kite-status-badge"
              className={`hidden sm:flex items-center gap-1.5 text-xs px-2 py-1 rounded border ${on ? "border-cyan-500/40 text-cyan-300" : "border-amber-500/40 text-amber-300"}`}>
              {on ? <PlugZap size={12} /> : <Plug size={12} />}
              {on ? `Kite · ${kite.user_id}` : kite?.configured ? "Kite logged out" : "Kite not set up"}
            </span>
            <span className="font-mono text-sm text-slate-300" data-testid="ist-clock">{clock} <span className="text-slate-500 text-xs">IST</span></span>
            <ThemeToggle />
            <button onClick={onLock} data-testid="lock-btn" title="Lock" className="p-2 rounded-md text-slate-400 hover:text-slate-100 hover:bg-[#1A2235]">
              <Lock size={16} />
            </button>
          </div>
        </div>
      </header>
      <LoginBanner kite={kite} />
      <main className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4">{children}</main>
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[#1E293B] bg-[#090D16]/95 backdrop-blur-xl grid grid-cols-5">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end data-testid={`m-${n.id}`}
            className={({ isActive }) => `py-2.5 flex flex-col items-center gap-0.5 text-[11px] ${isActive ? "text-cyan-300" : "text-slate-500"}`}>
            <n.icon size={18} />{n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
